/**
 * AI Testing Scenario Builder - Visual Interface
 * Comprehensive visual interface for creating and managing AI testing scenarios
 * Optimized for baby boomer demographic testing
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  TabPanel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup
} from '@mui/material';

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  AccessibilityNew as AccessibilityIcon,
  Smartphone as MobileIcon,
  Computer as DesktopIcon,
  Tablet as TabletIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// Types
interface ScenarioStep {
  id: string;
  name: string;
  description: string;
  agents: string[];
  validations: string[];
  expectedResults: string[];
  order?: number;
}

interface Scenario {
  id?: string;
  name: string;
  description: string;
  category: string;
  demographic: string;
  priority: 'low' | 'medium' | 'high';
  estimatedDuration: number;
  steps: ScenarioStep[];
  configuration: {
    targetUrl: string;
    platform: 'web' | 'mobile' | 'tablet' | 'all';
    environment: 'dev' | 'staging' | 'production';
    parallelExecution: boolean;
    screenshotCapture: boolean;
    videoRecording: boolean;
  };
  status?: string;
  metrics?: any;
}

interface ScenarioTemplate {
  id: string;
  name: string;
  category: string;
  demographic: string;
  priority: string;
  estimatedDuration: number;
  stepCount: number;
  description: string;
}

interface ExecutionResult {
  scenarioId: string;
  executionId: string;
  success: boolean;
  duration: number;
  successRate: number;
  results: any[];
  summary: any;
}

const AITestingScenarioBuilder: React.FC = () => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [templates, setTemplates] = useState<ScenarioTemplate[]>([]);
  const [executionResults, setExecutionResults] = useState<ExecutionResult[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [selectedDemographic, setSelectedDemographic] = useState('baby_boomer_tech_intermediate');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showAIGenerationDialog, setShowAIGenerationDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });

  // Demographics configuration
  const demographics = {
    'baby_boomer_tech_novice': {
      label: 'Tech Novice (55-65)',
      description: 'Low tech proficiency, needs guidance',
      color: '#ff9800',
      icon: '🔰'
    },
    'baby_boomer_tech_intermediate': {
      label: 'Tech Intermediate (66-75)',
      description: 'Medium tech proficiency, balanced approach',
      color: '#2196f3',
      icon: '⚖️'
    },
    'baby_boomer_tech_advanced': {
      label: 'Tech Advanced (76-80+)',
      description: 'High tech proficiency, feature focused',
      color: '#4caf50',
      icon: '🚀'
    },
    'accessibility_focused': {
      label: 'Accessibility Focused',
      description: 'Users with disabilities and assistive technology',
      color: '#9c27b0',
      icon: '♿'
    }
  };

  const categories = {
    'authentication': { label: 'Authentication & Onboarding', icon: '🔐' },
    'core_functionality': { label: 'Core Features & Functionality', icon: '⚙️' },
    'accessibility': { label: 'Accessibility & WCAG Compliance', icon: '♿' },
    'responsive_design': { label: 'Cross-Device Experience', icon: '📱' },
    'error_handling': { label: 'Error Handling & Recovery', icon: '🚨' },
    'performance': { label: 'Performance & Speed', icon: '⚡' },
    'custom': { label: 'Custom Scenarios', icon: '🎯' }
  };

  const availableAgents = [
    { id: 'ui_tester', name: 'UI Tester Agent', description: 'Authentication flows, login validation', icon: '🎯' },
    { id: 'accessibility_checker', name: 'Accessibility Checker', description: 'WCAG compliance, senior-friendly UI', icon: '♿' },
    { id: 'performance_analyzer', name: 'Performance Analyzer', description: 'Load testing, optimization analysis', icon: '⚡' },
    { id: 'visual_regression', name: 'Visual Regression', description: 'UI consistency, layout validation', icon: '👁️' },
    { id: 'cognitive_load_analyzer', name: 'Cognitive Load Analyzer', description: 'Mental complexity assessment', icon: '🧠' },
    { id: 'font_readability_tester', name: 'Font Readability Tester', description: 'Typography optimization for seniors', icon: '📖' },
    { id: 'voice_interface_tester', name: 'Voice Interface Tester', description: 'Speech recognition and audio quality', icon: '🎤' },
    { id: 'mobile_responsive_checker', name: 'Mobile Responsive Checker', description: 'Cross-device compatibility', icon: '📱' },
    { id: 'error_handling_validator', name: 'Error Handling Validator', description: 'Exception management testing', icon: '🚨' },
    { id: 'security_tester', name: 'Security Tester', description: 'Authentication security, data protection', icon: '🔒' },
    { id: 'memory_monitor', name: 'Memory Monitor', description: 'Performance monitoring and optimization', icon: '💾' },
    { id: 'browser_compatibility_checker', name: 'Browser Compatibility Checker', description: 'Cross-browser validation', icon: '🌐' },
    { id: 'data_entry_validator', name: 'Data Entry Validator', description: 'Form testing and validation', icon: '📝' },
    { id: 'navigation_analyzer', name: 'Navigation Analyzer', description: 'User flow and navigation patterns', icon: '🧭' },
    { id: 'content_comprehension_tester', name: 'Content Comprehension Tester', description: 'Text clarity and understanding', icon: '📚' },
    { id: 'multi_step_validator', name: 'Multi-Step Validator', description: 'Complex workflow testing', icon: '📋' }
  ];

  // Effects
  useEffect(() => {
    loadTemplates();
    loadScenarios();
  }, []);

  // API functions
  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/admin/ai-testing/scenarios/templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      showSnackbar('Failed to load templates', 'error');
    }
  };

  const loadScenarios = async () => {
    try {
      const response = await fetch('/api/admin/ai-testing/scenarios');
      if (response.ok) {
        const data = await response.json();
        setScenarios(data.scenarios || []);
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
      showSnackbar('Failed to load scenarios', 'error');
    }
  };

  const createScenarioFromTemplate = async (templateId: string, options: any) => {
    try {
      const response = await fetch('/api/admin/ai-testing/scenarios/from-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId, ...options })
      });

      if (response.ok) {
        const scenario = await response.json();
        setCurrentScenario(scenario.data);
        setScenarios(prev => [...prev, scenario.data]);
        setShowTemplateDialog(false);
        showSnackbar(`Created scenario: ${scenario.data.name}`, 'success');
        return scenario.data;
      } else {
        throw new Error('Failed to create scenario');
      }
    } catch (error) {
      console.error('Failed to create scenario from template:', error);
      showSnackbar('Failed to create scenario from template', 'error');
    }
  };

  const generateAIScenario = async (requirements: any) => {
    try {
      const response = await fetch('/api/admin/ai-testing/scenarios/ai-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requirements })
      });

      if (response.ok) {
        const scenario = await response.json();
        setCurrentScenario(scenario.data);
        setScenarios(prev => [...prev, scenario.data]);
        setShowAIGenerationDialog(false);
        showSnackbar(`Generated AI scenario: ${scenario.data.name}`, 'success');
        return scenario.data;
      } else {
        throw new Error('Failed to generate AI scenario');
      }
    } catch (error) {
      console.error('Failed to generate AI scenario:', error);
      showSnackbar('Failed to generate AI scenario', 'error');
    }
  };

  const executeScenario = async (scenarioId: string) => {
    try {
      setIsExecuting(true);
      setExecutionProgress(0);

      const response = await fetch(`/api/admin/ai-testing/scenarios/${scenarioId}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        setExecutionResults(prev => [...prev, result.data]);
        showSnackbar(`Scenario execution ${result.data.success ? 'completed' : 'failed'}`, 
                    result.data.success ? 'success' : 'warning');
        return result.data;
      } else {
        throw new Error('Failed to execute scenario');
      }
    } catch (error) {
      console.error('Failed to execute scenario:', error);
      showSnackbar('Failed to execute scenario', 'error');
    } finally {
      setIsExecuting(false);
      setExecutionProgress(0);
    }
  };

  // Helper functions
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const createNewScenario = () => {
    const newScenario: Scenario = {
      name: '',
      description: '',
      category: 'core_functionality',
      demographic: selectedDemographic,
      priority: 'medium',
      estimatedDuration: 300,
      steps: [],
      configuration: {
        targetUrl: 'https://app.lifescribe.com',
        platform: 'web',
        environment: 'staging',
        parallelExecution: false,
        screenshotCapture: true,
        videoRecording: false
      }
    };
    setCurrentScenario(newScenario);
    setActiveTab(1); // Switch to builder tab
  };

  const addStep = () => {
    if (!currentScenario) return;

    const newStep: ScenarioStep = {
      id: `step_${Date.now()}`,
      name: '',
      description: '',
      agents: [],
      validations: [],
      expectedResults: []
    };

    setCurrentScenario({
      ...currentScenario,
      steps: [...currentScenario.steps, newStep]
    });
  };

  const updateStep = (stepIndex: number, updatedStep: ScenarioStep) => {
    if (!currentScenario) return;

    const updatedSteps = currentScenario.steps.map((step, index) =>
      index === stepIndex ? updatedStep : step
    );

    setCurrentScenario({
      ...currentScenario,
      steps: updatedSteps
    });
  };

  const removeStep = (stepIndex: number) => {
    if (!currentScenario) return;

    const updatedSteps = currentScenario.steps.filter((_, index) => index !== stepIndex);
    setCurrentScenario({
      ...currentScenario,
      steps: updatedSteps
    });
  };

  const filteredTemplates = templates.filter(template => 
    filterCategory === 'all' || template.category === filterCategory
  );

  // Render components
  const renderTemplateCard = (template: ScenarioTemplate) => (
    <Card key={template.id} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {categories[template.category]?.icon} {template.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {template.description}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip 
                label={demographics[template.demographic]?.label || template.demographic}
                color="primary" 
                size="small"
                icon={<span>{demographics[template.demographic]?.icon}</span>}
              />
              <Chip 
                label={`${template.stepCount} steps`} 
                size="small" 
                variant="outlined"
              />
              <Chip 
                label={`~${Math.round(template.estimatedDuration / 60)} min`} 
                size="small" 
                variant="outlined"
              />
              <Chip 
                label={template.priority} 
                size="small" 
                color={template.priority === 'high' ? 'error' : template.priority === 'medium' ? 'warning' : 'default'}
              />
            </Box>
          </Box>
          <Box display="flex" gap={1} ml={2}>
            <Tooltip title="Preview Template">
              <IconButton size="small">
                <PreviewIcon />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                createScenarioFromTemplate(template.id, {
                  demographic: selectedDemographic,
                  targetUrl: 'https://app.lifescribe.com'
                });
              }}
              startIcon={<AddIcon />}
            >
              Use Template
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderStepBuilder = (step: ScenarioStep, stepIndex: number) => (
    <Accordion key={step.id || stepIndex}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" width="100%">
          <Typography variant="h6" flex={1}>
            Step {stepIndex + 1}: {step.name || 'Untitled Step'}
          </Typography>
          <Box display="flex" gap={1}>
            {step.agents.length > 0 && (
              <Chip label={`${step.agents.length} agents`} size="small" />
            )}
            {step.validations.length > 0 && (
              <Chip label={`${step.validations.length} validations`} size="small" />
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Step Name"
              value={step.name}
              onChange={(e) => updateStep(stepIndex, { ...step, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={step.description}
              onChange={(e) => updateStep(stepIndex, { ...step, description: e.target.value })}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>AI Testing Agents</InputLabel>
              <Select
                multiple
                value={step.agents}
                onChange={(e) => updateStep(stepIndex, { ...step, agents: e.target.value as string[] })}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => {
                      const agent = availableAgents.find(a => a.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={agent?.name || value} 
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {availableAgents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{agent.icon}</span>
                      <Box>
                        <Typography variant="body2">{agent.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {agent.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Validations
            </Typography>
            {step.validations.map((validation, vIndex) => (
              <Box key={vIndex} display="flex" gap={1} mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={validation}
                  onChange={(e) => {
                    const updatedValidations = step.validations.map((v, i) => 
                      i === vIndex ? e.target.value : v
                    );
                    updateStep(stepIndex, { ...step, validations: updatedValidations });
                  }}
                  placeholder="Enter validation requirement..."
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedValidations = step.validations.filter((_, i) => i !== vIndex);
                    updateStep(stepIndex, { ...step, validations: updatedValidations });
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                updateStep(stepIndex, { 
                  ...step, 
                  validations: [...step.validations, ''] 
                });
              }}
            >
              Add Validation
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Expected Results
            </Typography>
            {step.expectedResults.map((result, rIndex) => (
              <Box key={rIndex} display="flex" gap={1} mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={result}
                  onChange={(e) => {
                    const updatedResults = step.expectedResults.map((r, i) => 
                      i === rIndex ? e.target.value : r
                    );
                    updateStep(stepIndex, { ...step, expectedResults: updatedResults });
                  }}
                  placeholder="Enter expected result..."
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedResults = step.expectedResults.filter((_, i) => i !== rIndex);
                    updateStep(stepIndex, { ...step, expectedResults: updatedResults });
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                updateStep(stepIndex, { 
                  ...step, 
                  expectedResults: [...step.expectedResults, ''] 
                });
              }}
            >
              Add Expected Result
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => removeStep(stepIndex)}
              >
                Remove Step
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  const renderExecutionResults = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Execution Results
      </Typography>
      {executionResults.length === 0 ? (
        <Alert severity="info">
          No execution results yet. Run a scenario to see results here.
        </Alert>
      ) : (
        executionResults.map((result, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h6">
                    Execution #{result.executionId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Duration: {Math.round(result.duration / 1000)}s | Success Rate: {result.successRate.toFixed(1)}%
                  </Typography>
                </Box>
                <Chip
                  label={result.success ? 'Success' : 'Failed'}
                  color={result.success ? 'success' : 'error'}
                  icon={result.success ? <CheckIcon /> : <ErrorIcon />}
                />
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Step Results
              </Typography>
              {result.results.map((stepResult, stepIndex) => (
                <Box key={stepIndex} display="flex" alignItems="center" gap={1} mb={1}>
                  {stepResult.success ? 
                    <CheckIcon color="success" fontSize="small" /> : 
                    <ErrorIcon color="error" fontSize="small" />
                  }
                  <Typography variant="body2">
                    {stepResult.stepName} - {stepResult.success ? 'Passed' : 'Failed'}
                  </Typography>
                  {stepResult.successRate && (
                    <Chip 
                      label={`${(stepResult.successRate * 100).toFixed(0)}%`} 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
              ))}

              {result.summary.recommendations && result.summary.recommendations.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Recommendations
                  </Typography>
                  {result.summary.recommendations.map((rec, recIndex) => (
                    <Alert key={recIndex} severity={rec.priority === 'high' ? 'error' : 'warning'} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">{rec.title}</Typography>
                      <Typography variant="body2">{rec.description}</Typography>
                    </Alert>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        AI Testing Scenario Builder
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Create and manage comprehensive testing scenarios for baby boomer demographics
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Templates & Gallery" icon={<AssessmentIcon />} />
          <Tab label="Scenario Builder" icon={<SettingsIcon />} />
          <Tab label="Execution & Results" icon={<TimelineIcon />} />
        </Tabs>
      </Box>

      {/* Tab 0: Templates & Gallery */}
      {activeTab === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" gap={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Demographic Focus</InputLabel>
                <Select
                  value={selectedDemographic}
                  onChange={(e) => setSelectedDemographic(e.target.value)}
                >
                  {Object.entries(demographics).map(([key, demo]) => (
                    <MenuItem key={key} value={key}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{demo.icon}</span>
                        {demo.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Category Filter</InputLabel>
                <Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {Object.entries(categories).map(([key, cat]) => (
                    <MenuItem key={key} value={key}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{cat.icon}</span>
                        {cat.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowAIGenerationDialog(true)}
              >
                AI Generate
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={createNewScenario}
              >
                Create New
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Scenario Templates
              </Typography>
              {filteredTemplates.map(renderTemplateCard)}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab 1: Scenario Builder */}
      {activeTab === 1 && (
        <Box>
          {!currentScenario ? (
            <Alert severity="info">
              Select a template or create a new scenario to start building.
            </Alert>
          ) : (
            <>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Scenario Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Scenario Name"
                        value={currentScenario.name}
                        onChange={(e) => setCurrentScenario({
                          ...currentScenario,
                          name: e.target.value
                        })}
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={currentScenario.description}
                        onChange={(e) => setCurrentScenario({
                          ...currentScenario,
                          description: e.target.value
                        })}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={currentScenario.category}
                          onChange={(e) => setCurrentScenario({
                            ...currentScenario,
                            category: e.target.value
                          })}
                        >
                          {Object.entries(categories).map(([key, cat]) => (
                            <MenuItem key={key} value={key}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{cat.icon}</span>
                                {cat.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth margin="normal">
                        <InputLabel>Demographic</InputLabel>
                        <Select
                          value={currentScenario.demographic}
                          onChange={(e) => setCurrentScenario({
                            ...currentScenario,
                            demographic: e.target.value
                          })}
                        >
                          {Object.entries(demographics).map(([key, demo]) => (
                            <MenuItem key={key} value={key}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{demo.icon}</span>
                                {demo.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth margin="normal">
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={currentScenario.priority}
                          onChange={(e) => setCurrentScenario({
                            ...currentScenario,
                            priority: e.target.value as 'low' | 'medium' | 'high'
                          })}
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Testing Steps ({currentScenario.steps.length})
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={addStep}
                    >
                      Add Step
                    </Button>
                  </Box>
                  
                  {currentScenario.steps.length === 0 ? (
                    <Alert severity="info">
                      Add testing steps to define what should be tested in this scenario.
                    </Alert>
                  ) : (
                    currentScenario.steps.map((step, index) => renderStepBuilder(step, index))
                  )}
                </CardContent>
              </Card>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                >
                  Save Scenario
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  disabled={currentScenario.steps.length === 0 || isExecuting}
                  onClick={() => {
                    if (currentScenario.id) {
                      executeScenario(currentScenario.id);
                    }
                  }}
                >
                  {isExecuting ? 'Executing...' : 'Run Scenario'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Tab 2: Execution & Results */}
      {activeTab === 2 && (
        <Box>
          {isExecuting && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Scenario Execution in Progress
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={executionProgress} 
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {executionProgress}% complete
                </Typography>
              </CardContent>
            </Card>
          )}
          
          {renderExecutionResults()}
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AITestingScenarioBuilder;